import { DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { Flex, Table, Typography, theme, Button } from 'antd';
import React, { useState } from 'react';
import {
  ChargeBeePlanId,
  getDomainSchedulerSettingsMaxRules,
  getPlanById,
  isEnterprise,
} from '@prerender/billing-shared';

import { NewRuleModal } from './NewRuleModal';
import { createUsNumberFormat } from '../../../utils/createUsNumberFormat';
import { getTimeLabel } from '../utils/getTimeLabel';
import ButtonWithRole from '../../../components/ButtonWithRole';
import Role from '~features/PrerenderUser/Role';
import { useCharbeePlanId, usePrerenderUserFeatures } from '../../../hooks/usePrerenderUser';
import { type SchedulerSettingsResult } from '../api/schedulerSettingsApiSlice';
import { CustomTooltip } from '~components/Tooltip';
import { useUpgradePlan } from '~features/UpgradePlan';
import { useEvent } from '~features/events/hooks/useEvent';

type PatternBasedRulesProps = {
  schedulerSettings?: SchedulerSettingsResult['data'];
  cacheExpirationDaysInterval: [number, number];
  isLoading?: boolean;
  isAddSettingLoading?: boolean;
  onDeleteSetting: (id: string) => void;
  onAddSetting: (values: { cacheExpirationHours: number; rule: string }) => void;
};

function PatternBasedRules({
  schedulerSettings = [],
  cacheExpirationDaysInterval,
  isLoading,
  onDeleteSetting,
  onAddSetting,
  isAddSettingLoading,
}: PatternBasedRulesProps) {
  const [showModal, setShowModal] = useState(false);
  const chargebeePlanId = useCharbeePlanId();
  const availableFeatures = usePrerenderUserFeatures();
  const { token } = theme.useToken();
  const { openUpgradePlan } = useUpgradePlan();
  const { track } = useEvent();

  const onAddRule = () => {
    track('Cache Manager: New Expiration Rule Clicked');
    setShowModal(true)
  };

  const onSubmit = (values: { cacheExpirationHours: number; rule: string }) => {
    onAddSetting(values);
    setShowModal(false);
  };

  const maxRules = React.useMemo(
    () => getDomainSchedulerSettingsMaxRules(getPlanById(chargebeePlanId), availableFeatures),
    [availableFeatures, chargebeePlanId]
  );

  const isAddRuleDisabled = schedulerSettings.length >= maxRules;
  const displayUpsell = isAddRuleDisabled && !isEnterprise(chargebeePlanId); // if Enterprise they already have max rules, nothing to upsell

  return (
    <div>
      <NewRuleModal
        showModal={showModal}
        isAddSettingLoading={isAddSettingLoading}
        onSubmit={onSubmit}
        daysInterval={cacheExpirationDaysInterval}
        onCancel={() => setShowModal(false)}
      />
      <Flex justify="right" style={{ marginBottom: token.marginSM }}>
        <div>
          <Typography.Text strong style={{ marginRight: token.margin }}>
            {schedulerSettings.length}/{maxRules} rules used
          </Typography.Text>

          {displayUpsell ? (
            <CustomTooltip message={'To add up to 10 rules, upgrade to Enterprise plan'} placement="top">
              <Button
                type="default"
                variant="filled"
                color="blue"
                icon={<LockOutlined />}
                onClick={() =>
                  openUpgradePlan('domainManager', ChargeBeePlanId.EnterprisePlus, {
                    ctaLocation: 'Custom Cache Expiration Rules - Pattern-Based Rules Widget',
                    ctaName: 'Upgrade to Enterprise',
                  })
                }
              >
                Upgrade to Enterprise
              </Button>
            </CustomTooltip>
          ) : (
            <ButtonWithRole
              disabledFor={[Role.BillingManager, Role.Guest]}
              type="primary"
              onClick={onAddRule}
              disabled={isAddRuleDisabled}
            >
              Add rule
            </ButtonWithRole>
          )}
        </div>
      </Flex>
      <Table
        rowKey="id"
        dataSource={schedulerSettings}
        bordered
        pagination={false}
        loading={isLoading}
        style={{ marginBottom: 32 }}
        size="small"
      >
        <Table.Column title="Rule" dataIndex="rule" key="rule" />
        <Table.Column
          title="Cache Expiration"
          dataIndex="cache_freshness_hours"
          key="cache_freshness_hours"
          render={(_rules, record) => getTimeLabel(record.cache_freshness_hours)}
        />
        <Table.Column
          title="Number of pages"
          dataIndex="affected_urls"
          key="affected_urls"
          render={(_rules, record) => createUsNumberFormat(record.affected_urls)}
        />
        <Table.Column
          title=""
          dataIndex="actions"
          key="actions"
          render={(_rules, record) => (
            <ButtonWithRole
              disabledFor={[Role.BillingManager, Role.Guest]}
              onClick={() => {
                onDeleteSetting(record.id);
              }}
              danger
            >
              <DeleteOutlined />
            </ButtonWithRole>
          )}
        />
      </Table>
    </div>
  );
}

export default PatternBasedRules;
